/**
 * popularTripsApi.ts — seeded "popular trip" catalogue for the planner.
 *
 * GET /api/routes/popular            → summaries of the seeded road-trip routes
 * GET /api/routes/<id>/system-trip   → the materialised system Trip (ordered
 *                                      stops with float lat/lng) for a route
 * Both endpoints are public catalogue content — no auth needed, so anonymous
 * users can load a popular trip straight into the ladder as a draft.
 */

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

export interface PopularRouteSummary {
  id: string;
  name: string;
  from?: string;
  to?: string;
  distance?: string;
  duration?: string;
  campsite_count?: number;
  route_type?: string;
  /** Photos for the route, best first (Wikimedia Commons, our own parks). */
  collage_images?: string[];
}

export interface SystemTripStop {
  id?: string;
  entity_id?: number | null;
  name?: string;
  kind?: string;
  lat?: number | null;
  lng?: number | null;
  nights?: number | null;
  notes?: string | null;
  order_index?: number;
}

export interface SystemTrip {
  id: string;
  name?: string;
  routeId?: string;
  stops: SystemTripStop[];
}

export async function fetchPopularRoutes(limit = 24): Promise<PopularRouteSummary[]> {
  try {
    const res = await fetch(`${API_BASE}/routes/popular?limit=${limit}`);
    if (!res.ok) return [];
    const data = await res.json();
    const routes = data?.routes || data?.data || [];
    return Array.isArray(routes) ? routes.filter((r: PopularRouteSummary) => r && r.id && r.name) : [];
  } catch {
    return [];
  }
}

export async function fetchSystemTrip(routeId: string): Promise<SystemTrip | null> {
  try {
    const res = await fetch(`${API_BASE}/routes/${encodeURIComponent(routeId)}/system-trip`);
    if (!res.ok) return null;
    const data = await res.json();
    const trip = data?.data;
    if (!trip || !Array.isArray(trip.stops)) return null;
    return trip as SystemTrip;
  } catch {
    return null;
  }
}
